import {
  CSV_ACCEPT,
  csvFileValidator,
  FileUploadInput,
} from '@avanta/shared/components/FileUploadInput';
import { Button } from '@avanta/shared/components/ui/Button';
import { Dialog } from '@avanta/shared/components/ui/Dialog';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { CSV_MIME_TYPE } from '@avanta/shared/constants/storage';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { useFetchCentersByEnterprise } from '@avanta/shared/hooks/useFetchCentersByEnterprise';
import { useFetchDepartments } from '@avanta/shared/hooks/useFetchDepartments';
import { downloadFile } from '@avanta/shared/utils/storage';
import { DownloadIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CREATE_EMPLOYEE_STATUS } from 'constants/employees';
import { useEnterpriseId } from 'stores/user';
import { ImportErrorState } from './ImportErrorState';
import exampleCsvContent from './avantamynd_import_patients.csv?raw';
import { useCreateEmployees } from './useCreateEmployees';

const EXAMPLE_CSV_FILENAME = 'avantamynd_import_patients.csv';

export const ImportPatientsDialog = (): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { hideDialog } = useDialogContext();
  const enterpriseId = useEnterpriseId();
  const {
    data: createEmployeesResponse,
    mutate: createEmployees,
    isPending: isCreatingEmployees,
    reset: resetCreateEmployees,
  } = useCreateEmployees();
  const [file, setFile] = useState<File | null>(null);

  const { data: centers } = useFetchCentersByEnterprise({
    enterpriseId,
    isProfileSetup: false,
  });

  const { data: departments } = useFetchDepartments({
    centerId: centers?.[0]?.value || '',
    isProfileSetup: false,
  });

  const handleImportPatients = (): void => {
    if (!enterpriseId || !file) {
      return;
    }
    createEmployees({
      file,
      enterpriseId,
      departmentId: departments?.[0]?.value || '',
    });
  };

  const handleFileChange = (selectedFile: File | null): void => {
    setFile(selectedFile);
    resetCreateEmployees();
  };

  const handleDownloadExampleCsv = (): void => {
    downloadFile({
      content: exampleCsvContent,
      filename: EXAMPLE_CSV_FILENAME,
      mimeType: CSV_MIME_TYPE,
    });
  };

  const importResults = createEmployeesResponse?.data ?? [];
  const hasImportIssues = importResults.some(
    result => result.status !== CREATE_EMPLOYEE_STATUS.CREATED
  );

  return (
    <Dialog
      open
      enableOutsideClick={false}
      title={t('ImportPatientsDialog.title')}
      className="w-3xl"
    >
      {hasImportIssues ? (
        <ImportErrorState results={importResults} />
      ) : (
        <div className="flex flex-col gap-6 w-full">
          <p className="text-denim-500 leading-5">{t('ImportPatientsDialog.description')}</p>
          <div className="flex flex-col gap-2">
            <FileUploadInput
              value={file}
              onChange={handleFileChange}
              accept={CSV_ACCEPT}
              validator={csvFileValidator}
              helperText={t('ImportPatientsDialog.helper.text')}
              rejectionMessage={t('ImportPatientsDialog.rejection.message')}
            />
            {!file && (
              <Button
                variant="link"
                className="w-fit text-denim-500 has-[>svg]:p-0"
                onClick={handleDownloadExampleCsv}
                size="lg"
                icon="leading"
              >
                <DownloadIcon className="size-5 text-denim-500" strokeWidth={0.75} />
                {t('ImportPatientsDialog.downloadCSV')}
              </Button>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="destructive_terciary"
              type="button"
              className="w-fit"
              onClick={hideDialog}
              size="lg"
              icon="leading"
            >
              <XIcon className="size-6 text-red-400" strokeWidth={0.75} />
              {t('Common.cancel', { ns: LOCIZE_NS.COMMON })}
            </Button>
            <Button
              type="button"
              className="min-w-40"
              disabled={!file}
              variant="secondary"
              onClick={handleImportPatients}
              isLoading={isCreatingEmployees}
              size="lg"
            >
              {t('InsuredPersonsList.addPersonByCSV.cta')}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
};
